import { useSelector } from 'react-redux';
import {
  selectCurrentPicklistData,
  selectPicklistAssetsData,
  selectPicklistLooseItemsData,
  selectPicklistAssembliesData
} from 'pages/PicklistAndLeavelist/PicklistSummary/PicklistDetailed/selectors';
import { useUserConfig } from 'hooks/useUserConfig';
import { PicklistAssetStatusNames, PicklistStatusNames } from 'helpers/Enums';

export function usePicklistPermissions() {
  const currentPicklist = useSelector(selectCurrentPicklistData());
  const assets = useSelector(selectPicklistAssetsData());
  const looseItems = useSelector(selectPicklistLooseItemsData());
  const assemblies = useSelector(selectPicklistAssembliesData());
  const { status = {}, location = {}, createdByUser = {} } = currentPicklist;

  const {
    isAdminUser,
    isOperationsManagerUser,
    isFieldTechUser,
    isEntityCreatorUser,
    isLocationInConfig
  } = useUserConfig();

  const isPicklistOpen = status?.name === PicklistStatusNames.open;
  const isPicklistCompleted = status?.name === PicklistStatusNames.completed;

  const isRemoveAvailable = isAdminUser || isEntityCreatorUser(createdByUser.email);

  const isEditAvailable = (() => {
    if (isAdminUser) return true;
    if (isOperationsManagerUser) {
      return isEntityCreatorUser(createdByUser.email) || isLocationInConfig(location?.id);
    }
    return isEntityCreatorUser(createdByUser.email);
  })();

  const areManagementButtonsAvailable = (() => {
    if (isAdminUser) return true;
    if (isOperationsManagerUser || isFieldTechUser) {
      return isEntityCreatorUser(createdByUser.email) || isLocationInConfig(location?.id);
    }
    return isEntityCreatorUser(createdByUser.email);
  })();

  const isPicklistAssetLocked = ({ status }) =>
    [PicklistAssetStatusNames.receivedAtJobSite, PicklistAssetStatusNames.receivedAtYard].includes(
      status?.name
    );
  const isRemovePicklistAssetAvailable = (() => {
    if (isAdminUser) return true;
    if (isOperationsManagerUser || isFieldTechUser) {
      return isEntityCreatorUser(createdByUser.email) || isLocationInConfig(location?.id);
    }
    return isEntityCreatorUser(createdByUser.email);
  })();
  const isCreateBoLAvailable =
    isAdminUser ||
    isOperationsManagerUser ||
    isFieldTechUser ||
    isEntityCreatorUser(createdByUser.email);

  const isPicklistAssetSelectionAvailable = isCreateBoLAvailable || isRemovePicklistAssetAvailable;

  const isCellEditable = (() => {
    if (isAdminUser) return true;
    if (isOperationsManagerUser || isFieldTechUser) {
      return isEntityCreatorUser(createdByUser.email) || isLocationInConfig(location?.id);
    }
    return isEntityCreatorUser(createdByUser.email);
  })();

  return {
    canRemove: isRemoveAvailable && isPicklistOpen,
    canEdit: isEditAvailable && isPicklistOpen,
    canAddItems: !isPicklistCompleted && areManagementButtonsAvailable,
    canChangePicklistStatus: !isPicklistCompleted && areManagementButtonsAvailable,
    shouldDisableStatusButton:
      isPicklistOpen && !assets?.length && !looseItems?.length && !assemblies?.length,
    // picklist assets
    isCreateBoLAvailable,
    isRemovePicklistAssetAvailable,
    canRemovePicklistAsset: (row) => !isPicklistAssetLocked(row) && isRemovePicklistAssetAvailable,
    isPicklistAssetSelectionAvailable,
    canEditDrCode: (row) => isCellEditable && !isPicklistAssetLocked(row) && row?.asset?.id,
    canEditLooseItem: (row) =>
      isCellEditable && !isPicklistAssetLocked(row) && row?.locationLooseItem?.id,
    canEditNotes: isCellEditable,
    canEditPickupDate: isCellEditable,
    canEditTruckOrTrailer: isCellEditable,
    canEditAssetStatus: (row) => isCellEditable && !isPicklistAssetLocked(row) && !isPicklistOpen,
    canUploadFile: isCellEditable,
    canRemoveAsset: (row) => isRemovePicklistAssetAvailable && !isPicklistAssetLocked(row),
    // additional
    isPicklistOpen,
    isPicklistCompleted,
    isPicklistAssetLocked
  };
}
